import { useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { toReadableUnitName } from '../../../constants/units';
import { useUnitContext } from '../../../contexts/UnitContext';
import { MapContext } from '../../../contexts/MapContext';
import {
  BASE_LAYER_CONTOURS_QUERY_PARAM,
  BASE_LAYER_DARK_QUERY_PARAM,
  BASE_LAYER_QUERY_PARAM,
  BASE_LAYER_ROADS_QUERY_PARAM,
  HIDDEN_LAYER_QUERY_PARAM,
  HIDDEN_SECTION_QUERY_PARAM,
} from '../constants';

/**
 * Returns a link to the FieldsPreview page that includes information in the search params about units & visibility.
 */
export const useFieldsPreviewLink = () => {
  const { farmId } = useParams();

  const { units } = useUnitContext();

  const {
    layers: { sortedLayers },
    fields: { fieldSections },
    baseMapState: { baseLayer, contour, roads, dark },
  } = useContext(MapContext);

  const params = useMemo(() => {
    const result = new URLSearchParams();

    // Add param for units
    const readableUnitName = toReadableUnitName(units);
    result.set('units', readableUnitName);

    // Add param for hidden layers
    sortedLayers.forEach(({ shown }, i) => {
      if (!shown) result.append(HIDDEN_LAYER_QUERY_PARAM, i);
    });

    // Add params for the base layer
    result.set(BASE_LAYER_QUERY_PARAM, baseLayer);
    if (contour) result.set(BASE_LAYER_CONTOURS_QUERY_PARAM, 1);
    if (roads) result.set(BASE_LAYER_ROADS_QUERY_PARAM, 1);
    if (dark) result.set(BASE_LAYER_DARK_QUERY_PARAM, 1);

    // Add param for hidden sections
    fieldSections.forEach(({ shown }, i) => {
      if (!shown) result.append(HIDDEN_SECTION_QUERY_PARAM, i);
    });

    return result.toString();
  }, [fieldSections, sortedLayers, units, baseLayer, contour, roads, dark]);

  return `/app/farm/${farmId}/preview?${params}`;
};
